'use client';

import { useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { useAdminForm } from '@/hooks/useAdminForm';
import { ErrorAlert } from '@/components/admin/shared';
import { AuthShell, AuthNotice } from '@/components/admin/auth/AuthShell';

interface ForgotPasswordFormData {
  email: string;
}

/**
 * Forgot-password screen — Linear/Stripe 2026 pattern.
 *
 * Two visual states share the AuthShell chrome:
 *   1. Form (default): single email field + "Link senden" CTA
 *   2. Link-sent: emerald notice + "Zurück zur Anmeldung" link
 *
 * Both states reuse the same eyebrow/heading slot so the page doesn't
 * jump when the user submits. The previous version mounted a totally
 * different card per state which felt disjointed.
 */
export default function ForgotPasswordContent() {
  const t = useTranslations('admin.forgotPassword');
  const tAuth = useTranslations('admin.auth');
  const searchParams = useSearchParams();
  const tenantId = searchParams?.get('tenantId');
  const [emailSent, setEmailSent] = useState(false);

  const {
    formData,
    loading: isLoading,
    error,
    handleChange,
    handleSubmit,
    clearError,
  } = useAdminForm<ForgotPasswordFormData>({
    initialData: { email: '' },
    validate: (data) => {
      if (!data.email) return t('errors.missingEmail');
      return null;
    },
    onSubmit: async (data) => {
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: data.email, tenantId }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || t('errors.sendError'));
      setEmailSent(true);
    },
  });

  const loginHref = `/admin/login${tenantId ? `?tenantId=${tenantId}` : ''}`;

  return (
    <AuthShell
      eyebrow={tAuth('secureArea')}
      heading={tAuth('resetHeading')}
      subtitle={tAuth('resetSubtitle')}
      footer={
        <p className="text-center">
          {tAuth('rememberedLogin')}{' '}
          <Link
            href={loginHref}
            className="font-medium text-blue-600 underline-offset-2 hover:underline"
          >
            {tAuth('signIn')}
          </Link>
        </p>
      }
    >
      {emailSent ? (
        <div className="space-y-3">
          <AuthNotice tone="success">{tAuth('resetLinkSent')}</AuthNotice>
          <Button variant="outline" asChild className="w-full">
            <Link href={loginHref}>{tAuth('signIn')}</Link>
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <ErrorAlert error={error} onDismiss={clearError} />

          <div className="space-y-1.5">
            <Label htmlFor="email">{t('form.emailLabel')}</Label>
            <Input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('form.emailPlaceholder')}
              autoComplete="email"
              required
            />
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {tAuth('resetSubmitting')}
              </>
            ) : (
              tAuth('resetSubmit')
            )}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
